import React, { memo, useCallback } from 'react'
import { Button } from '@material-ui/core'
import clsx from 'clsx'
import fileDownload from 'js-file-download'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import Grid from '@material-ui/core/Grid'
import InputLabel from '@material-ui/core/InputLabel'
import { makeStyles } from '@material-ui/core/styles'
import NativeSelect from '@material-ui/core/NativeSelect'
import SettingsPanel from '../../components/settings/SettingsPanel'
import '../../components/settings/password/password.scss'
import { NO_AREA } from '../../data/areas'
import moment from 'moment'
import LOG from '../../logging/Logger'
import { FOCUS } from '../../data/focus'

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  },
  selectEmpty: {
    marginTop: theme.spacing(2)
  },
  formHint: {
    fontSize: '0.6rem',
    lineHeight: 1.4,
    margin: '-5px auto 5px',
    color: '#999',

    error: {
      color: '#c00',
      fontSize: '0.8rem'
    }
  }
}))

const EXPORT_VERSION = '1.1'

const SettingsExport = memo(
  ({
    getExportableArea,
    getExportableProject,
    getExportableRepeat,
    getExportableTag,
    getExportableTodo,
    getAreas,
    getProjects,
    getRepeats,
    getTags,
    getTodos
  }) => {
    LOG.debug('Render: SettingsExport')

    const classes = useStyles()
    const [state, setState] = React.useState({
      format: 'native'
    })

    const handleChange = useCallback(
      event => {
        const name = event.target.name
        setState({
          ...state,
          [name]: event.target.value
        })
      },
      [state]
    )

    const clearUnexportable = useCallback(item => {
      item.owner = undefined
      item.clientId = undefined
      item._version = undefined
      item._lastChangedAt = undefined
      item._deleted = undefined
      item.attachments = undefined
      return item
    }, [])

    const exportData = useCallback(() => {
      const areas = getAreas()
        .filter(area => area.id !== NO_AREA.id)
        .map(area => clearUnexportable(getExportableArea(area)))

      const projects = getProjects().map(project =>
        clearUnexportable(getExportableProject(project))
      )

      const repeatingTodos = getTodos(FOCUS.REPEATING.id, true, true)
      const repeats = getRepeats()
        .filter(
          repeat =>
            repeatingTodos.findIndex(todo => todo.repeat === repeat.id) !== -1
        )
        .map(repeat => {
          return {
            ...clearUnexportable(getExportableRepeat(repeat)),
            todo: clearUnexportable(
              getExportableTodo(
                repeatingTodos.find(todo => todo.repeat === repeat.id)
              )
            )
          }
        })

      const tags = getTags().map(tag =>
        clearUnexportable(getExportableTag(tag))
      )

      const todos = getTodos(null, true, true)
        .filter(todo => todo.list !== FOCUS.REPEATING.id)
        .map(todo => clearUnexportable(getExportableTodo(todo)))

      const data = {
        version: EXPORT_VERSION,
        areas,
        projects,
        repeats,
        tags,
        todos
      }
      fileDownload(
        JSON.stringify(data, null, 2),
        `gettingfocused_${moment().format('YYYYMMDDHHmmss')}.json`
      )
    }, [
      clearUnexportable,
      getAreas,
      getExportableArea,
      getExportableProject,
      getExportableRepeat,
      getExportableTag,
      getExportableTodo,
      getProjects,
      getRepeats,
      getTags,
      getTodos
    ])

    return (
      <div>
        <SettingsPanel title='Export'>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <span className={clsx('d-block', classes.formHint)}>
                We think you should you have access to all your important data
                whenever you want. We would recommend using the native format as
                it'll make it much easier to come back to us.
              </span>
            </Grid>
            <Grid item xs={12}>
              <FormControl className={classes.formControl}>
                <InputLabel htmlFor='format-native-helper'>Format</InputLabel>
                <NativeSelect
                  value={state.format}
                  onChange={handleChange}
                  inputProps={{
                    name: 'format',
                    id: 'format-native-helper'
                  }}
                >
                  <option value={'native'}>Native</option>
                  <option disabled={true} value={'csv'}>
                    Comma separated (coming soon)
                  </option>
                </NativeSelect>
                <FormHelperText>
                  Which format to you want to export your data in?
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl className={classes.formControl}>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={exportData}
                >
                  Export
                </Button>
              </FormControl>
            </Grid>
          </Grid>
        </SettingsPanel>
      </div>
    )
  }
)

export default SettingsExport
